import { RegisteredModule } from '../../services/module-registry.service';

/** What a dashboard tile needs, independent of where the module came from. */
export interface ModuleCardModel {
  readonly id: string;

  /**
   * Translation key of the module name, from the module's own dictionary.
   *
   * For a module that failed to resolve there is no manifest to read one from,
   * and this carries its configured id instead. Running an id through the
   * translate pipe returns it unchanged, which is the intended fallback.
   */
  readonly titleKey: string;

  readonly descriptionKey?: string;
  readonly icon: string;
  readonly version?: string;
  readonly category?: string;

  /** Whether the tile links anywhere. */
  readonly available: boolean;

  /** Translation key explaining why it does not, for the tiles that do not. */
  readonly noteKey?: string;

  /**
   * The licensing verdict, for modules that have one. Absent for modules
   * outside the UMC catalog — no relationship, no indicator.
   */
  readonly license?: 'licensed' | 'noSeat' | 'unlicensed';
}

const NOTE_KEYS: Record<string, string> = {
  missing: 'module.note.missing',
  invalid: 'module.note.invalid',
  failed: 'module.note.failed',
};

/**
 * Describes a registered module as a tile.
 *
 * Modules that failed to resolve have no manifest — there is nothing to read a
 * name or an icon from, so the tile falls back to the configured id and says
 * what went wrong. Hiding them instead would leave an operator wondering where
 * the module went; a tile that explains itself is the point.
 */
export function toModuleCard(module: RegisteredModule): ModuleCardModel {
  if (module.status === 'ready' && module.manifest) {
    return {
      id: module.id,
      titleKey: module.manifest.titleKey,
      descriptionKey: module.manifest.descriptionKey,
      icon: module.manifest.icon,
      version: module.manifest.version,
      category: module.category,
      available: true,
    };
  }

  return {
    id: module.id,
    titleKey: module.id,
    icon: 'pi pi-exclamation-triangle',
    category: module.category,
    available: false,
    noteKey: NOTE_KEYS[module.status],
  };
}
